/**
 * @module xhr
 * @description Module for handling XHR requests and DOM manipulation using the Fetch API.
 * Includes functionality for making POST requests, updating DOM elements, and managing scroll behavior.
 */

/**
 * @function makeRequest
 * @description Makes a POST request and updates DOM content with the response
 * @param {string} locationID - Target element ID to update content
 * @param {string} url - Endpoint URL for the request
 * @param {Object} [parameters=null] - Request body parameters
 * @param {string} [scrollToElementID=null] - Element ID to scroll to after update
 * @param {boolean} [replaceElement=false] - Whether to replace or update inner content
 * @throws {Error} If network response is not OK
 *
 * @example
 * xhr.makeRequest('contentDiv', '/api/data', {id: 123}, 'topDiv', false);
 */

/**
 * @function closeDiv
 * @description Empties the contents of a specified DOM element
 * @param {string} locationID - Element ID to clear
 *
 * @example
 * xhr.closeDiv('contentDiv');
 */

/**
 * @private
 * @note Safari compatibility: Uses promise chaining instead of async/await
 * @note Includes 500ms delay for scroll behavior to allow content loading
 * @note Integrates with tippySetup module for tooltip initialization
 */
const xhr = (function () {
    /**
     * This function makes an XHR request and updates the content of a specified element.
     *
     *
     * @param {string} locationID - The ID of the element where the content will be updated.
     * @param {string} url - The URL to make the XHR request to.
     * @param {Object} parameters - (optional) The parameters to include in the request body.
     * @param {string} scrollToElementID - (optional) The ID of the element to scroll to after the content is updated.
     * @param {boolean} replaceElement - (optional) TRUE = content will replace element locationID instead of putting it inside.
     * @returns {void}
     * @throws {Error} If the network response is not ok.
     */
    function makeRequest(locationID, url, parameters = null, scrollToElementID = null, replaceElement = false) {
        const targetElement = document.getElementById(locationID);
        if (targetElement === null) {
            return;
        }
        const data = new FormData();
        Object.keys(parameters).forEach(function (key, index) {
            data.append(key, parameters[key]);
        });

        // ! Use of promise chaining on fetch is required as safari is not executing async/await correctly.
        fetch(url, {
            method: "post",
            body: data,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Network response was not OK");
                }
                return response.text();
            })
            .then((content) => {
                if (content) {
                    if (replaceElement === true) {
                        const newNodes = new DOMParser().parseFromString(content, "text/html");
                        targetElement.replaceWith(newNodes.body.firstChild);
                    } else {
                        targetElement.innerHTML = content;
                    }
                }
            })
            .catch((error) => {
                console.error("Error:", error);
            })
            .finally(() => {
                tippySetup.startTippy();
                if (scrollToElementID !== null) {
                    // Wait 500ms before scrolling to allow time for div to be 100% loaded
                    // ! Cannot use await with sleep function due to safari issues
                    setTimeout(() => {
                        const scrollElement = document.getElementById(scrollToElementID);
                        if (scrollElement !== null) {
                            scrollElement.scrollIntoView({ behavior: "smooth" });
                        }
                    }, 500);
                }
            });
    }

    async function makeAPIRequest(url, parameters = null, submitElementID = null) {
        let result = null;

        // Get all DOM elements
        const submitButton = document.querySelector("#" + submitElementID) ?? null;

        // disabled the submit button

        // Create an FormData object
        let formData = new FormData();

        if (submitButton) {
            formData.append(submitButton.name, submitButton.value);
            submitButton.setAttribute("disabled", "disabled");
        }
        if (parameters) {
            Object.keys(parameters).forEach(function (key, index) {
                formData.append(key, parameters[key]);
            });
        }

        try {
            const response = await fetch(url, {
                method: "POST",
                body: formData,
            });
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const data = await response.json();

            if (data === null) {
                result = { responseCode: -1, status: "error", message: "data missing" };
            } else {
                result = { responseCode: data.responseCode, status: data.status, message: data.message, data: data };
            }
            if (submitButton) {
                submitButton.removeAttribute("disabled");
            }
        } catch (error) {
            console.error(error);
            result = { responseCode: -1, status: "error", message: error.message };
            if (submitButton) {
                submitButton.removeAttribute("disabled");
            }
            return result;
        }

        return result;
    }

    /**
     * This function is for removing all contents in a specific element
     * @param {string} locationID elementID of the element to empty
     */
    function closeDiv(locationID) {
        document.getElementById(locationID).innerHTML = "";
    }

    return {
        makeRequest: makeRequest,
        makeAPIRequest: makeAPIRequest,
        closeDiv: closeDiv,
    };
})();

export { xhr };
